import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import http from "@/lib/api";
import Lightbox from "@/components/Lightbox";
import SmartImage from "@/components/SmartImage";

const CATS = [
    { key: "all", label: "Toutes" },
    { key: "portraits", label: "Portraits" },
    { key: "asiatique", label: "Asiatique" },
    { key: "paysages", label: "Paysages & Animaux" },
    { key: "nu", label: "Nu artistique" },
];

export default function Gallery() {
    const [items, setItems] = useState([]);
    const [cat, setCat] = useState("all");
    const [active, setActive] = useState(null);

    useEffect(() => {
        const params = cat === "all" ? {} : { category: cat };
        http.get("/artworks", { params }).then(({ data }) => setItems(data));
    }, [cat]);

    return (
        <main data-testid="gallery-page" className="pt-32 pb-24 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mb-16 max-w-4xl"
                >
                    <div className="overline mb-4">Galerie</div>
                    <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-6">
                        Les œuvres de<br /><span className="italic-serif text-gilt">Patrick Mathurin</span>
                    </h1>
                    <p className="text-bone-200 text-lg leading-relaxed max-w-2xl">
                        Une collection en constante évolution. Cliquez sur une œuvre pour la découvrir en détail
                        — et, lorsqu'il existe, le film de sa réalisation.
                    </p>
                </motion.div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 md:gap-8 mb-16 border-b border-white/5 pb-6">
                    {CATS.map((c) => (
                        <button
                            key={c.key}
                            data-testid={`filter-${c.key}`}
                            onClick={() => setCat(c.key)}
                            className={`relative text-[11px] md:text-xs uppercase tracking-[0.22em] py-2 transition-colors ${cat === c.key ? "text-gilt" : "text-bone-300 hover:text-bone-50"
                                }`}
                        >
                            {c.label}
                            {cat === c.key && <span className="absolute -bottom-[25px] left-0 right-0 h-px bg-gilt" />}
                        </button>
                    ))}
                    <span className="ml-auto text-xs text-bone-400">{items.length} œuvres</span>
                </div>

                {/* Masonry */}
                <div className="masonry">
                    {items.map((a, i) => (
                        <motion.button
                            key={a.id}
                            data-testid={`artwork-tile-${a.id}`}
                            onClick={() => setActive(a)}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                            className="art-tile group w-full text-left block"
                        >
                            <div className="overflow-hidden bg-ink-800">
                                <SmartImage src={a.image_url} videoUrl={a.video_url} alt={a.title} className="w-full h-auto object-cover" fallback={a.title} />
                            </div>
                            <div className="pt-4 pb-2 flex items-baseline justify-between gap-4">
                                <div>
                                    <div className="serif text-xl group-hover:text-gilt transition-colors">{a.title}</div>
                                    <div className="text-[10px] text-bone-400 uppercase tracking-[0.25em] mt-1">{a.category} {a.year ? `· ${a.year}` : ""}</div>
                                </div>
                                <span className="text-gilt opacity-0 group-hover:opacity-100 transition-opacity serif text-2xl">→</span>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {items.length === 0 && (
                    <div className="text-center py-32 text-bone-400">Aucune œuvre dans cette catégorie pour le moment.</div>
                )}
            </div>

            {active && <Lightbox artwork={active} onClose={() => setActive(null)} />}
        </main>
    );
}
